import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/atoms/select";

interface Brand {
  id: number;
  name: string;
  branches?: Branch[];
}

interface Branch {
  id: number;
  name: string;
}

interface MapControlsProps {
  brands: Brand[];
  selectedBrandId: string;
  selectedBranchId: string;
  showDrivers: boolean;
  showServiceZone: boolean;
  onBrandChange: (id: string) => void;
  onBranchChange: (id: string) => void;
  onToggleDrivers: () => void;
  onToggleServiceZone: () => void;
  onShowBranchInfo?: () => void;
}

export const MapControls = ({
  brands,
  selectedBrandId,
  selectedBranchId,
  showDrivers,
  showServiceZone,
  onBrandChange,
  onBranchChange,
  onToggleDrivers,
  onToggleServiceZone,
  onShowBranchInfo,
}: MapControlsProps) => {
  const selectedBrand = brands.find((b) => b.id.toString() === selectedBrandId);
  const branches = selectedBrand?.branches || [];

  return (
    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-4 p-4">
      {/* Selectors Section */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto">
        <Select value={selectedBrandId} onValueChange={onBrandChange}>
          <SelectTrigger className="w-full sm:min-w-[160px] sm:max-w-[200px]">
            <SelectValue placeholder="Selecciona un negocio" />
          </SelectTrigger>
          <SelectContent>
            {Array.isArray(brands) && brands.length > 0 ? (
              brands.map((brand) => (
                <SelectItem key={brand.id} value={brand.id.toString()}>
                  {brand.name}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="empty" disabled>
                Sin negocios
              </SelectItem>
            )}
          </SelectContent>
        </Select>

        <Select
          value={selectedBranchId}
          onValueChange={onBranchChange}
          disabled={!Array.isArray(branches) || branches.length === 0}
        >
          <SelectTrigger className="w-full sm:min-w-[160px] sm:max-w-[200px]">
            <SelectValue placeholder="Selecciona una sucursal" />
          </SelectTrigger>
          <SelectContent>
            {!Array.isArray(branches) || branches.length === 0 ? (
              <SelectItem value="empty" disabled>
                Sin sucursales
              </SelectItem>
            ) : (
              branches.map((branch) => {
                if (!branch || typeof branch.id === "undefined") {
                  return null;
                }
                return (
                  <SelectItem key={branch.id} value={branch.id.toString()}>
                    {branch.name}
                  </SelectItem>
                );
              })
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Toggle Controls Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 w-full lg:w-auto">
        {/* Toggle Conductores */}
        <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
          <span className="text-sm text-gray-700 whitespace-nowrap">
            Conductores
          </span>
          <button
            onClick={onToggleDrivers}
            className={`relative inline-flex h-6 w-11 rounded-full border-2 focus:ring-green-500 focus:outline-none transition-colors ${
              showDrivers ? "bg-green-600" : "bg-gray-200"
            }`}
            role="switch"
            aria-checked={showDrivers}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform ${
                showDrivers ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Toggle Zona de Servicio */}
        <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
          <span className="text-sm text-gray-700 whitespace-nowrap">
            Zona de servicio
          </span>
          <button
            onClick={onToggleServiceZone}
            className={`relative inline-flex h-6 w-11 rounded-full border-2 focus:ring-blue-500 focus:outline-none transition-colors ${
              showServiceZone ? "bg-blue-600" : "bg-gray-200"
            }`}
            role="switch"
            aria-checked={showServiceZone}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform ${
                showServiceZone ? "translate-x-5" : "translate-x-0"
              }`}
            />
          </button>
        </div>

        {/* Show Branch Info Button */}
        {onShowBranchInfo && selectedBranchId && (
          <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
            <button
              onClick={onShowBranchInfo}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Ver info sucursal
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export type { Brand, Branch };
